// ============================================================
// agent_tool_outcome.js — 툴 실행 결과 → "판정 카드" 분류(순수 로직)
// ============================================================
// 역할: 런타임 SSE tool_call 페이로드 한 건을 보고, 채팅에 띄울 판정 카드의 종류와
//       문구 키를 정한다. 차단(정책 거절)·위임 해제·사람 승인 대기·발주 완료를
//       서로 다른 시각으로 갈라 주는 단일 판정 지점이다.
//
// 왜 필요한가:
//   기존 렌더는 tool_call 을 전부 <details class="ac-tool"> 한 종류로만 찍어서,
//   "한도를 넘어 거절됐다"(limit_exceeded)와 "사람 승인을 기다린다"(pending_approval)와
//   "발주됐다"(placed)가 화면에서 구분되지 않았다. 결과 JSON 을 펼쳐 읽어야만
//   무슨 일이 일어났는지 알 수 있었다 — 정책이 일했다는 사실이 안 보였다.
//
// 왜 컨트롤러 밖으로 뺐나:
//   판정 규칙(상태·에러 코드 → 카드 종류)이 이 연출의 계약 그 자체라 회귀가 나면
//   사용자가 다시 JSON 을 읽게 된다. DOM·Stimulus 없이 검증할 수 있도록 순수 함수로
//   분리해 node 테스트(test/javascript/agent_tool_outcome_test.js)가 직접 태운다.
//   (agent_expiry_cards.js 와 같은 관례다.)
//
// 페이로드 출처(agent-runtime · 커머스 앱, 무접촉):
//   - 정책 차단:    { name:'commerce.order', status:'error',  result:{ error:<code>, reason, hint } }
//   - 위임 해제:    { name, status:'denied', reason:'delegation_revoked'|'slot_user_mode'|... }
//   - 사람 승인 대기: { status:'ok', result:{ status:'pending_approval', reason, order, hint } }
//   - 발주 완료:    { status:'ok', result:{ status:'placed', order } }
//   HTTP 상태 숫자는 페이로드에 없다 — 상태 판정은 전부 위 필드로만 한다.
//
// 주의사항:
//   - 반환 titleKey/bodyKey 는 i18n 키 이름(agent_chats.verdict.<key>)이다. 문구를 여기 두지 않는다
//     (webxcom 은 6개 언어 강제라 JS 에 문장을 박으면 그 자리만 한국어로 굳는다).
//   - status:'error' 판정은 commerce.* 툴에만 적용한다. 다른 툴의 실패까지 "정책 거절"로
//     읽히면 오탐이다 — 그 경우 kind:'plain' 으로 기존 렌더를 그대로 쓴다.
//   - 분류에서 빠지면 반드시 kind:'plain' 이다. 일반 검색·견적 성공에 카드가 뜨면 회귀다.
// ============================================================

// 정책 판정 카드를 붙일 툴 이름 접두어. 커머스(주문·결제) 툴만 정책 거절 문구를 받는다.
const COMMERCE_PREFIX = "commerce."

// 차단 코드(result.error) → 본문 문구 키. 목록 밖 코드는 code_default 로 떨어진다.
// 문장은 앱(alimall) 활동 로그의 결정론 요약과 같은 말로 맞춘다 — 같은 사건이 화면마다
// 다른 용어로 보이면 사용자가 두 번 해석해야 한다. 시스템 용어(403·scope·policy)는 쓰지 않는다.
const BLOCKED_BODY_KEYS = {
  limit_exceeded:         "code_limit_exceeded",
  total_exceeds_declared: "code_total_exceeds_declared",
  daily_limit_exceeded:   "code_daily_limit_exceeded",
  monthly_limit_exceeded: "code_monthly_limit_exceeded",
  per_day_exceeded:       "code_per_day_exceeded",
  ship_to_not_allowed:    "code_ship_to_not_allowed",
  provider_not_allowed:   "code_provider_not_allowed",
  quote_not_found:        "code_quote_not_found",
  invalid_state:          "code_invalid_state"
}

// denied 사유(reason) → 본문 문구 키.
const REVOKED_BODY_KEYS = {
  delegation_revoked:  "revoked_delegation_revoked",
  slot_user_mode:      "revoked_slot_user_mode",
  introspect_inactive: "revoked_introspect_inactive",
  need_consent:        "revoked_need_consent"
}

// 제목은 사유에 따라 갈린다. "위임이 해제되어 중단했다"는 delegation_revoked 전용 문장이고,
// 사람 모드·동의 누락 같은 사유에 그 제목을 쓰면 일어나지 않은 일을 보고하는 셈이 된다.
const REVOKED_TITLE_KEY = "revoked_title"
const REVOKED_TITLE_OTHER_KEY = "revoked_title_other"

// 아무 카드도 붙이지 않는 기본 판정(기존 <details> 렌더만 쓴다).
const PLAIN = { kind: "plain", code: null, titleKey: null, bodyKey: null, note: "", orderId: "" }

// ── 툴 결과 판정 ─────────────────────────────────────────────
// WHAT: tool_call 페이로드 하나를 카드 종류 + 문구 키 + 런타임 원문(note)으로 번역한다.
// @param d {Object} SSE tool_call 이벤트 payload
// @returns {{kind: String, code: String|null, titleKey: String|null,
//            bodyKey: String|null, note: String, orderId: String}}
//   kind: 'blocked'(정책 거절) | 'revoked'(위임 해제) | 'pending'(사람 승인 대기)
//         | 'placed'(발주 접수) | 'plain'(카드 없음)
//   note: 런타임이 준 사람이 읽을 사유 문장(있을 때만). i18n 문구 아래 작은 글씨로 쓴다.
export function classifyToolOutcome(d) {
  if (!d || typeof d !== "object") return PLAIN

  // 위임 해제·모드 전환은 툴 이름과 무관하다 — 권한이 없으면 무슨 툴이든 못 움직인다.
  if (d.status === "denied") {
    const reason = typeof d.reason === "string" ? d.reason : ""
    return {
      kind: "revoked",
      code: reason || null,
      titleKey: reason === "delegation_revoked" ? REVOKED_TITLE_KEY : REVOKED_TITLE_OTHER_KEY,
      bodyKey: REVOKED_BODY_KEYS[reason] || "revoked_default",
      note: "",
      orderId: ""
    }
  }

  const result = (d.result && typeof d.result === "object") ? d.result : null

  // 정책 거절 — commerce.* 의 error 만. 다른 툴의 실패는 기존 렌더를 그대로 둔다(오탐 방지).
  if (d.status === "error") {
    if (!isCommerceTool(d.name) || !result) return PLAIN
    const code = typeof result.error === "string" ? result.error : ""
    return {
      kind: "blocked",
      code: code || null,
      titleKey: "blocked_title",
      bodyKey: BLOCKED_BODY_KEYS[code] || "code_default",
      note: textOf(result.reason),
      orderId: ""
    }
  }

  if (!result) return PLAIN

  // 사람 승인 대기 — 한도 안이지만 앱의 승인 기준(hitl_threshold)을 넘어 사람에게 넘어간 주문.
  // 런타임 고위험 사전 확인(confirm_required)과는 다른 사건이라 카드도 따로 띄운다.
  if (result.status === "pending_approval") {
    return {
      kind: "pending",
      code: null,
      titleKey: "pending_title",
      bodyKey: "pending_default",
      note: textOf(result.reason),
      orderId: orderIdOf(result.order)
    }
  }

  // 발주 접수 — 이 데모는 dry_run 이라 문구를 "시뮬레이션 발주"로 고정한다(실결제 아님).
  if (result.status === "placed") {
    return {
      kind: "placed",
      code: null,
      titleKey: "placed_title",
      bodyKey: null,
      note: "",
      orderId: orderIdOf(result.order)
    }
  }

  return PLAIN
}

// commerce.order / commerce.quote 처럼 커머스 네임스페이스 툴인지.
function isCommerceTool(name) {
  return typeof name === "string" && name.indexOf(COMMERCE_PREFIX) === 0
}

// 런타임이 준 사유·힌트 문장을 문자열로만 받는다(객체가 오면 버린다 — JSON 을 다시 보여주지 않는다).
function textOf(value) {
  return typeof value === "string" ? value : ""
}

// 주문 식별자 — 앱마다 키 이름이 갈려(order.id / orderId / order_id) 순서대로 찾는다.
// 문자열 자체로 오는 경우(order: "ORD-1")도 받는다.
function orderIdOf(order) {
  if (typeof order === "string") return order
  if (!order || typeof order !== "object") return ""
  const id = order.id || order.orderId || order.order_id
  return (typeof id === "string" || typeof id === "number") ? String(id) : ""
}
